// ============================================
// FILE: src/utils/categoryShelves.js
// ============================================
//
// Groups the home page's products into one shelf per category, each with that
// category's own banners — the web counterpart of the mobile app's
// buildCategoryShowcases.
//
// Sub-categories fold into their parent: a "Women's Dresses" product sits on
// the "Women" shelf, and the parent shelf shows its children's banners too.

const idOf = (value) => (value === null || value === undefined || value === '' ? null : String(value));

// Banners aimed at the product page don't belong on a home shelf.
const SHELF_BANNER_POSITIONS = new Set(['home', 'category', 'all', '']);

const productCategoryId = (product) => idOf(
  product?.category_id ?? (typeof product?.category === 'object' ? product?.category?.id : null)
);

const productCategoryName = (product) => (
  typeof product?.category === 'object' ? product?.category?.name : product?.category
) || null;

/**
 * @param {Array} categories  Category rows from /categories (flat, with parent_id).
 * @param {Array} products    Products to place (already filtered for the page).
 * @param {Array} banners     Active banner rows from /banners.
 * @returns {Array<{category, products, banners}>} Non-empty shelves, in category order.
 */
export const buildCategoryShelves = (categories = [], products = [], banners = []) => {
  const list = Array.isArray(categories) ? categories : [];
  const byId = new Map(list.map((c) => [idOf(c.id), c]));

  // A category is a shelf when it has no parent — or its parent isn't in the
  // visible list, so its products would otherwise have nowhere to go.
  const shelfFor = new Map(); // category id → shelf category id
  const resolveShelf = (category) => {
    let current = category;
    const seen = new Set();
    while (current && idOf(current.parent_id) && byId.has(idOf(current.parent_id))) {
      if (seen.has(idOf(current.id))) break; // guard against a parent loop
      seen.add(idOf(current.id));
      current = byId.get(idOf(current.parent_id));
    }
    return current;
  };
  list.forEach((c) => shelfFor.set(idOf(c.id), idOf(resolveShelf(c)?.id)));

  const nameToId = new Map(list.map((c) => [String(c.name || '').toLowerCase(), idOf(c.id)]));

  const shelves = new Map();
  list.forEach((c) => {
    if (shelfFor.get(idOf(c.id)) === idOf(c.id)) {
      shelves.set(idOf(c.id), { category: c, products: [], banners: [] });
    }
  });

  (Array.isArray(products) ? products : []).forEach((product) => {
    let categoryId = productCategoryId(product);
    if (!categoryId || !byId.has(categoryId)) {
      const name = productCategoryName(product);
      categoryId = name ? nameToId.get(String(name).toLowerCase()) : null;
    }
    const shelf = categoryId ? shelves.get(shelfFor.get(categoryId)) : null;
    if (shelf) shelf.products.push(product);
  });

  // Individual banners only: a shelf shows banners pinned to its category (or
  // one of its sub-categories), never the store-wide ones — those are the
  // page hero's. The parent's own banners lead, then its children's.
  (Array.isArray(banners) ? banners : []).forEach((banner) => {
    const categoryId = idOf(banner?.category_id);
    if (!categoryId || !byId.has(categoryId)) return;
    if (!SHELF_BANNER_POSITIONS.has(String(banner?.position || '').toLowerCase())) return;
    const shelf = shelves.get(shelfFor.get(categoryId));
    if (!shelf) return;
    if (categoryId === idOf(shelf.category.id)) {
      const ownCount = shelf.banners.filter((b) => idOf(b.category_id) === categoryId).length;
      shelf.banners.splice(ownCount, 0, banner);
    } else {
      shelf.banners.push(banner);
    }
  });

  return [...shelves.values()].filter((shelf) => shelf.products.length > 0);
};

export default buildCategoryShelves;
